// CustomerWithdrawalsReport.jsx — every withdrawal request of one customer (drill-down from the customer dialog).

import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Button, Chip } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { format, subDays } from 'date-fns';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
import CustomerReportShell, {
  DateRangeFilter,
  ReportGrid,
  SectionCard,
  displayVpa,
  formatINR,
  formatISTDateTime,
  useCanViewCustomerSensitive,
  validateDateRange,
} from '../../components/CustomerReportShell';

const SOURCE_LABEL = { cashback: 'UPI Rewards', referral: 'Referral Rewards', cash: 'Cash Rewards' };
const STATUS_COLOR = { completed: 'success', pending: 'warning', processing: 'info', failed: 'error' };
const statusLabel = (s) => (s === 'failed' ? 'Rejected' : String(s || '-').replace(/^./, (c) => c.toUpperCase()));

const CustomerWithdrawalsReport = () => {
  const { customerId } = useParams();
  const canViewSensitive = useCanViewCustomerSensitive();
  const [startDate, setStartDate] = useState(subDays(new Date(), 365));
  const [endDate, setEndDate] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const rangeError = validateDateRange(startDate, endDate);
    if (rangeError) { setError(rangeError); return; }
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const { data } = await axios.post(
        `${API_BASE_URL}/customerReport/withdrawals`,
        { customerId, startDate: format(startDate, 'yyyy-MM-dd'), endDate: format(endDate, 'yyyy-MM-dd') },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setCustomerName(data.customerName || '');
      setTotals(data.totals || null);
      setRows((data.withdrawals || []).map((w, i) => ({ ...w, serialNumber: i + 1 })));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || 'An error occurred while fetching withdrawals.');
      setRows([]);
      setTotals(null);
    }
    setLoading(false);
  }, [customerId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const bankText = (b) => (b ? `${b.bankName ? `${b.bankName} • ` : ''}${b.accountMasked} • ${b.ifsc}` : '-');

  const handlePrint = () => {
    const doc = new jsPDF('l', 'pt', 'a4');
    const center = doc.internal.pageSize.getWidth() / 2;
    doc.setFontSize(16);
    doc.text('Customer Withdrawals Report', center, 40, { align: 'center' });
    doc.setFontSize(12);
    if (customerName) doc.text(`Customer: ${customerName}`, center, 60, { align: 'center' });
    doc.text(`From ${format(startDate, 'dd-MM-yyyy')} to ${format(endDate, 'dd-MM-yyyy')}`, center, customerName ? 80 : 60, { align: 'center' });
    doc.autoTable({
      startY: customerName ? 100 : 80,
      head: [['S.No.', 'Requested', 'UPI id', 'Type', 'Amount', 'Fee', 'Net', 'Status', 'Bank account']],
      body: rows.map((w) => [
        w.serialNumber, formatISTDateTime(w.requestedAt), displayVpa(w.vpaId, canViewSensitive), SOURCE_LABEL[w.source] || w.source,
        `Rs.${w.amount.toFixed(2)}`, `Rs.${w.feeAmount.toFixed(2)}`, w.netAmount === null ? '-' : `Rs.${w.netAmount.toFixed(2)}`,
        statusLabel(w.status), bankText(w.bank),
      ]),
      styles: { fontSize: 8 },
    });
    doc.save(`customer_withdrawals_${format(new Date(), 'yyyyMMdd')}.pdf`);
  };

  const columns = [
    { field: 'serialNumber', headerName: 'S.No.', width: 70 },
    { field: 'requestedAt', headerName: 'Requested', width: 180, valueGetter: (v) => formatISTDateTime(v) },
    { field: 'vpaId', headerName: 'UPI id', width: 190, valueGetter: (v) => displayVpa(v, canViewSensitive) },
    { field: 'source', headerName: 'Type', width: 140, valueGetter: (v) => SOURCE_LABEL[v] || v },
    {
      field: 'amount', headerName: 'Amount', width: 110, type: 'number',
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatINR(p.value)}</Typography>,
    },
    { field: 'feeAmount', headerName: 'Fee', width: 90, type: 'number', valueGetter: (v) => formatINR(v) },
    { field: 'netAmount', headerName: 'To bank', width: 110, valueGetter: (v) => (v === null || v === undefined ? '-' : formatINR(v)) },
    {
      field: 'status', headerName: 'Status', width: 120,
      renderCell: (p) => <Chip size="small" label={statusLabel(p.value)} color={STATUS_COLOR[p.value] || 'default'} />,
    },
    { field: 'bank', headerName: 'Bank account', width: 300, sortable: false, valueGetter: (v) => bankText(v) },
    { field: 'processedAt', headerName: 'Processed', width: 180, valueGetter: (v) => (v ? formatISTDateTime(v) : '-') },
    { field: 'transactionId', headerName: 'Bank ref / Txn id', width: 200, valueGetter: (v) => v || '-' },
    { field: 'note', headerName: 'Admin note', width: 220, valueGetter: (v) => v || '-' },
  ];

  const t = totals || {};
  return (
    <CustomerReportShell
      title="Customer Withdrawals"
      subtitle={`Withdrawal requests · ${format(startDate, 'dd MMM yyyy')} to ${format(endDate, 'dd MMM yyyy')}`}
      icon={<AccountBalanceWalletIcon />}
      customerId={customerId}
      customerName={customerName}
      actions={(
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={rows.length === 0} sx={{ fontWeight: 700 }}>
          Print Report
        </Button>
      )}
      filters={<DateRangeFilter startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate} />}
      error={error}
      onRetry={load}
      statsLoading={loading}
      stats={[
        { label: 'Withdrawn', value: formatINR(t.completed && t.completed.amount), hint: `${(t.completed && t.completed.count) || 0} completed`, color: 'success', icon: <CheckCircleOutlineIcon /> },
        { label: 'Pending', value: formatINR(t.pending && t.pending.amount), hint: `${(t.pending && t.pending.count) || 0} waiting for the admin`, color: 'warning', icon: <HourglassEmptyIcon /> },
        { label: 'Rejected', value: formatINR(t.failed && t.failed.amount), hint: `${(t.failed && t.failed.count) || 0} rejected`, color: 'error', icon: <HighlightOffIcon /> },
      ]}
    >
      <SectionCard title="Withdrawal Requests" subtitle={`${rows.length} request${rows.length === 1 ? '' : 's'}`}>
        <ReportGrid rows={rows} columns={columns} loading={loading} getRowId={(r) => r.id} emptyTitle="No withdrawals found" emptyMessage="Try a different date range." />
      </SectionCard>
    </CustomerReportShell>
  );
};

export default CustomerWithdrawalsReport;
